// ---------- notifications push (Android seulement) ----------
// Le canal PWA/web n'a pas de push : ce module ne fait rien hors de l'APK (isNative()), exactement
// comme initPlatform() dans src/platform.ts. Il ne demande RIEN au démarrage — la permission
// système est réclamée depuis Réglages, sur un geste explicite (cf. enablePush()) : un prompt
// posé au lancement est celui que Chrome/Android pénalisent, et c'est aussi la moins bonne façon
// de présenter une fonctionnalité à quelqu'un qui n'a encore rien demandé.
//
// Le token FCM est tenu par le plugin, pas par nous : il change (réinstall, restauration sur un
// nouvel appareil), et c'est le plugin qui le sait le premier via son listener `registration`.

import { PushNotifications } from '@capacitor/push-notifications';
import { ensureUser } from './account';
import { registerPushToken, unregisterPushToken } from './api';
import { isNative } from './platform';

let lastToken: string | null = null;

/** À appeler une fois au démarrage, natif seulement. Câble seulement les listeners — pas de
 *  permission demandée ici, cf. le commentaire d'en-tête. */
export function initPush(): void {
  if (!isNative()) return;

  void PushNotifications.addListener('registration', ({ value }) => {
    lastToken = value;
    void (async () => {
      const user = await ensureUser();
      if (!user) return; // hors ligne : le prochain enablePush() (ou le prochain lancement) réessaiera
      try {
        await registerPushToken(user, value);
      } catch {
        /* pas grave : sans confirmation serveur, le joueur ne reçoit simplement rien pour l'instant */
      }
    })();
  });

  void PushNotifications.addListener('registrationError', () => {
    /* l'appareil n'a pas de Google Play Services à jour, ou le service est indisponible — le jeu
     * continue sans notifications, ce n'est jamais bloquant */
  });
}

/**
 * Demande la permission si nécessaire puis enregistre l'appareil. Retourne `false` sans rien
 * casser si la permission est refusée ou l'enregistrement impossible — c'est l'appelant
 * (src/prefs.ts) qui décide alors de ne pas allumer l'interrupteur.
 */
export async function enablePush(): Promise<boolean> {
  if (!isNative()) return false;
  const current = await PushNotifications.checkPermissions();
  let state = current.receive;
  if (state === 'prompt' || state === 'prompt-with-rationale') {
    state = (await PushNotifications.requestPermissions()).receive;
  }
  if (state !== 'granted') return false;
  await PushNotifications.register();
  return true;
}

/** Coupe l'interrupteur : le token connu de ce lancement est retiré côté serveur. Ne révoque pas
 *  la permission système elle-même (impossible depuis l'app) — juste l'envoi. */
export async function disablePush(): Promise<void> {
  if (!lastToken) return;
  const user = await ensureUser();
  if (!user) return;
  try {
    await unregisterPushToken(user, lastToken);
  } catch {
    /* le token restera en base jusqu'au prochain envoi qui le trouvera mort et le purgera */
  }
}
